import { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { getKitsForTinyAccountId } from 'store/kits';
import { getImpersonateId } from 'store/user';
import { getKits } from 'store/shared';

import { kitResultsViewable, sortKitsByResultsViewableOrLatest } from '@repo/utils';
import { Spinfinity, Typography } from 'components';
import ConsultsBooking from 'views/Consults/ConsultsBooking/ConsultsBooking';
import ConsultsInfoPage from 'views/Consults/ConsultsInfo/ConsultsInfoPage';
import ConsultsSelectMethod from 'views/Consults/ConsultsSelectMethod/ConsultsSelectMethod';
import { NoKitRegistered, NoKitResults } from './';
import { ResultsReadyRoutes } from './ResultsReadyRoutes';
import { SurveysRoutes } from './Surveys/SurveysRoutes';
import { VersionUpdate } from './VersionUpdate/VersionUpdate';

import useFeature from 'services/features/useFeature';
import styles from './Results.module.scss';

export type ResultsParams = {
  kitId: string;
};

export const ResultsPage = () => {
  const {
    currentKit,
    currentKitId,
    currentKitHasResults,
    currentTinyAccountId,
  } = useCurrentKitAndTinyAccount();
  const kits = useSelector(getKits);
  const impersonateId = useSelector(getImpersonateId);
  const defaultKitId = sortKitsByResultsViewableOrLatest(kits)[0]?.id;
  const kitsForCurrentTinyAccount = useSelector(
    getKitsForTinyAccountId(currentTinyAccountId),
  );

  const hasOtherKitsWithResults = kitsForCurrentTinyAccount.some(
    kit =>
      kit.id !== currentKitId && kitResultsViewable(kit, !!impersonateId),
  );

  let NextAction = <ResultsReadyRoutes />;
  if (defaultKitId && !currentKitId) {
    NextAction = <Navigate to={`/results/${defaultKitId}`} replace />;
  } else if (!kitsForCurrentTinyAccount?.length) {
    NextAction = <NoKitRegistered />;
  } else if (!currentKitHasResults || !currentKit?.date_survey_completed) {
    NextAction = (
      <NoKitResults hasOtherKitsWithResults={hasOtherKitsWithResults} />
    );
  }

  return (
    <>
      <Suspense fallback={<Spinfinity />}>
        <div className={styles.contentWrapper}>
          <div className={styles.resultsContainer} id='results-container'>
            <div className={styles.nextActionContainer}>{NextAction}</div>
            {currentKitHasResults && !!currentKit?.date_survey_completed && (
              <footer className={styles.resultsFooter}>
                <Typography variant='body-s' serif>
                  Note: This report is not a diagnosis. It reflects the results
                  of a wellness microbiome test and are intended to educate you
                  about the microbes in your body.
                </Typography>
                <Typography variant='body-s' serif>
                  The microbes we talk about in your report, and their
                  associations, are based on emerging scientific research. Your
                  report insights may change over time to reflect the most
                  up-to-date research and data analysis. Additional sections may
                  appear as we launch new insights.
                </Typography>
                <Typography variant='body-s' serif>
                  Tiny Health tests are not intended to diagnose or treat
                  disease, and are not a substitute for professional medical
                  advice. Feel free to share this with your practitioner and
                  always consult with them before implementing a new dietary or
                  supplement change.
                </Typography>
              </footer>
            )}
          </div>
        </div>
      </Suspense>
    </>
  );
};

export const ResultsRoutes = () => {
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );

  return (
    <Routes>
      <Route path='update' element={<VersionUpdate />} />
      {showGroupCalls && (
        <Route
          path=':kitId/consults/select-method'
          element={<ConsultsSelectMethod />}
        />
      )}
      <Route path=':kitId/consults/book' element={<ConsultsBooking />} />
      <Route path=':kitId/consults' element={<ConsultsInfoPage />} />
      <Route path=':kitId/surveys/*' element={<SurveysRoutes />} />
      <Route path=':kitId/*' element={<ResultsPage />} />
      <Route path='/' element={<ResultsPage />} />
    </Routes>
  );
};
